import { useMemo, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import type { AuditEntryListItem } from '@/api/v2-activity';
import {
  ENTITY_PHRASES,
  actionVerb,
  actionDotClass,
  entityTypeLabel,
  formatDiffValue,
  meaningfulDiff,
} from './labels';
import { cn } from '@/lib/utils';

/** Taille minimale d'une suite d'entrées de même nature pour la replier. */
const GROUP_THRESHOLD = 4;
/** Écart max entre deux entrées d'une même rafale utilisateur+action+type. */
const RUN_MAX_GAP_MS = 180_000;
/** Nombre de changements de champs affichés sous la phrase. */
const MAX_INLINE_CHANGES = 3;

interface ActivityFeedProps {
  items: AuditEntryListItem[];
  loading: boolean;
  onOpenEntry: (entryId: string) => void;
}

type FeedBlock =
  | { kind: 'entry'; item: AuditEntryListItem }
  | { kind: 'group'; key: string; items: AuditEntryListItem[] };

interface DaySection {
  dayKey: string;
  dayLabel: string;
  blocks: FeedBlock[];
}

function dayLabelOf(iso: string): { key: string; label: string } {
  const date = new Date(iso);
  const key = date.toLocaleDateString('fr-FR');
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  if (key === today.toLocaleDateString('fr-FR')) return { key, label: "Aujourd'hui" };
  if (key === yesterday.toLocaleDateString('fr-FR')) return { key, label: 'Hier' };
  return {
    key,
    label: date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  };
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Deux entrées adjacentes appartiennent-elles à la même rafale ? Même
 * transaction, OU même utilisateur + action + type d'objet à cadence machine
 * (les traitements V2 type import émettent une transaction PAR élément, le
 * transactionId seul ne suffit pas). Doit rester aligné sur la logique de
 * l'ActivityEnricher backend, qui saute l'enrichissement de ces entrées.
 */
function isSameRun(a: AuditEntryListItem, b: AuditEntryListItem): boolean {
  if (a.transactionId && a.transactionId === b.transactionId) return true;
  if (a.actorId === b.actorId && a.action === b.action && a.entityType === b.entityType) {
    return (
      Math.abs(new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()) <=
      RUN_MAX_GAP_MS
    );
  }
  return false;
}

/**
 * Découpe les entrées (déjà triées par date desc) en sections par jour, en
 * repliant les rafales (imports, actions en masse) en « opération groupée ».
 */
function buildSections(items: AuditEntryListItem[]): DaySection[] {
  const sections: DaySection[] = [];

  for (let i = 0; i < items.length; ) {
    const item = items[i];
    const { key: dayKey, label: dayLabel } = dayLabelOf(item.occurredAt);

    let section = sections[sections.length - 1];
    if (!section || section.dayKey !== dayKey) {
      section = { dayKey, dayLabel, blocks: [] };
      sections.push(section);
    }

    let runEnd = i + 1;
    while (
      runEnd < items.length &&
      isSameRun(items[runEnd - 1], items[runEnd]) &&
      dayLabelOf(items[runEnd].occurredAt).key === dayKey
    ) {
      runEnd++;
    }

    if (runEnd - i >= GROUP_THRESHOLD) {
      section.blocks.push({
        kind: 'group',
        key: `${item.id}-${i}`,
        items: items.slice(i, runEnd),
      });
      i = runEnd;
    } else {
      for (let k = i; k < runEnd; k++) {
        section.blocks.push({ kind: 'entry', item: items[k] });
      }
      i = runEnd;
    }
  }

  return sections;
}

/** Pastille colorée selon l'action (reprend la palette des badges). */
function ActionDot({ action }: { action: string }) {
  return (
    <span className={cn('mt-1.5 h-2.5 w-2.5 rounded-full shrink-0', actionDotClass(action))} />
  );
}

function ActorName({ label }: { label: string | null }) {
  if (!label) {
    return <span className="font-semibold text-gray-500 dark:text-slate-400">Le système</span>;
  }
  return <span className="font-semibold">{label}</span>;
}

/** Tournure « objet » de la phrase, avec le nom en gras quand on l'a. */
function EntityPhrase({ item }: { item: AuditEntryListItem }) {
  const phrase = ENTITY_PHRASES[item.entityType];
  const label = item.entityLabel ?? null;

  if (!phrase) {
    const generic = entityTypeLabel(item.entityType);
    return label ? (
      <>
        {generic} <strong>« {label} »</strong>
      </>
    ) : (
      <>un élément « {generic} »</>
    );
  }
  if (!label) {
    return <>{phrase.indefinite}</>;
  }
  if (phrase.person) {
    return (
      <>
        la fiche de <strong>{label}</strong>
        <span className="text-gray-400 dark:text-slate-500"> ({phrase.noun})</span>
      </>
    );
  }
  return (
    <>
      {phrase.definite} <strong>« {label} »</strong>
    </>
  );
}

/** Lignes « Champ : avant → après » sous la phrase (modifications). */
function InlineChanges({ item }: { item: AuditEntryListItem }) {
  // À la création/suppression, le diff contient tous les champs : le détail
  // suffit, la phrase se suffit à elle-même. Les changements purement
  // techniques (références identiques une fois formatées) sont écartés.
  const diff =
    item.action === 'CREATE' || item.action === 'DELETE' ? [] : meaningfulDiff(item.diff ?? []);
  if (diff.length === 0) {
    return null;
  }

  const visible = diff.slice(0, MAX_INLINE_CHANGES);
  const remaining = diff.length - visible.length;

  return (
    <div className="mt-1 space-y-0.5">
      {visible.map((change) => (
        <div key={change.field} className="text-sm text-gray-600 dark:text-slate-300">
          <span className="text-gray-500 dark:text-slate-400">{change.label ?? change.field} :</span>{' '}
          <span className="text-red-700 dark:text-red-400 line-through decoration-red-300">
            {formatDiffValue(change.before)}
          </span>
          <span className="text-gray-400"> → </span>
          <span className="text-green-700 dark:text-green-400 font-medium">
            {formatDiffValue(change.after)}
          </span>
        </div>
      ))}
      {remaining > 0 && (
        <div className="text-xs text-gray-400 dark:text-slate-500">
          + {remaining} autre{remaining > 1 ? 's' : ''} champ{remaining > 1 ? 's' : ''} — cliquer
          pour le détail
        </div>
      )}
    </div>
  );
}

function FeedEntry({
  item,
  onOpen,
  inGroup = false,
}: {
  item: AuditEntryListItem;
  onOpen: (id: string) => void;
  inGroup?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className={cn(
        'w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 transition-colors',
        'hover:bg-gray-50 dark:hover:bg-slate-800/60',
        inGroup && 'py-1.5'
      )}
    >
      <span className="w-11 shrink-0 text-xs text-gray-400 dark:text-slate-500 tabular-nums pt-1">
        {timeOf(item.occurredAt)}
      </span>
      <ActionDot action={item.action} />
      <span className="min-w-0">
        <span className="text-sm text-gray-800 dark:text-slate-200">
          <ActorName label={item.actorLabel} /> {actionVerb(item.action)}{' '}
          <EntityPhrase item={item} />
        </span>
        {!inGroup && <InlineChanges item={item} />}
      </span>
    </button>
  );
}

/** Résumé d'une opération groupée : « 138 élèves, 138 utilisateurs… ». */
function groupSummary(items: AuditEntryListItem[]): string {
  const counts = new Map<string, number>();
  for (const item of items) {
    const phrase = ENTITY_PHRASES[item.entityType];
    const noun = phrase?.noun ?? entityTypeLabel(item.entityType).toLowerCase();
    counts.set(noun, (counts.get(noun) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([noun, count]) => `${count} ${noun}${count > 1 && !noun.endsWith('s') ? 's' : ''}`)
    .join(', ');
}

/**
 * Phrase de tête d'un groupe : rafale homogène → « X a modifié 25 élèves »,
 * sinon « X a effectué une opération groupée — N événements (…) ».
 */
function GroupSentence({ items }: { items: AuditEntryListItem[] }) {
  const first = items[0];
  const homogeneous = items.every(
    (item) => item.action === first.action && item.entityType === first.entityType
  );

  if (homogeneous) {
    const phrase = ENTITY_PHRASES[first.entityType];
    const noun = phrase?.noun ?? entityTypeLabel(first.entityType).toLowerCase();
    const plural = noun.endsWith('s') || noun.endsWith('x') ? noun : `${noun}s`;
    return (
      <>
        <ActorName label={first.actorLabel} /> {actionVerb(first.action)}{' '}
        <strong>
          {items.length} {plural}
        </strong>
      </>
    );
  }

  return (
    <>
      <ActorName label={first.actorLabel} /> a effectué une opération groupée
      <span className="text-gray-500 dark:text-slate-400">
        {' '}
        — {items.length} événements ({groupSummary(items)})
      </span>
    </>
  );
}

function FeedGroup({
  blockKey,
  items,
  onOpen,
}: {
  blockKey: string;
  items: AuditEntryListItem[];
  onOpen: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const first = items[0];

  return (
    <div className="rounded-lg border border-gray-100 dark:border-slate-700/60 bg-gray-50/50 dark:bg-slate-800/30">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left flex items-start gap-3 px-3 py-2"
        aria-expanded={expanded}
        aria-controls={`feed-group-${blockKey}`}
      >
        <span className="w-11 shrink-0 text-xs text-gray-400 dark:text-slate-500 tabular-nums pt-1">
          {timeOf(first.occurredAt)}
        </span>
        <Layers className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
        <span className="min-w-0 flex-1 text-sm text-gray-800 dark:text-slate-200">
          <GroupSentence items={items} />
        </span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
        )}
      </button>
      {expanded && (
        <div
          id={`feed-group-${blockKey}`}
          className="border-t border-gray-100 dark:border-slate-700/60 py-1"
        >
          {items.map((item) => (
            <FeedEntry key={item.id} item={item} onOpen={onOpen} inGroup />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Fil d'activité narratif : lecture « qui a fait quoi » en phrases françaises,
 * groupée par jour, opérations en masse repliées. Chaque entrée s'ouvre dans
 * le panneau de détail (seconde lecture, technique).
 */
export function ActivityFeed({ items, loading, onOpenEntry }: ActivityFeedProps) {
  const sections = useMemo(() => buildSections(items), [items]);

  if (loading) {
    return (
      <div className="space-y-3 rounded-xl border bg-white dark:bg-slate-900 shadow-md p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 w-11" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-xl border bg-white dark:bg-slate-900 shadow-md py-16 text-center text-gray-500 dark:text-slate-400">
        Aucune activité sur la période sélectionnée
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 shadow-md px-3 py-2">
      {sections.map((section) => (
        <div key={section.dayKey} className="py-2">
          <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
            {section.dayLabel}
          </div>
          <div className="space-y-1">
            {section.blocks.map((block) =>
              block.kind === 'entry' ? (
                <FeedEntry key={block.item.id} item={block.item} onOpen={onOpenEntry} />
              ) : (
                <FeedGroup
                  key={block.key}
                  blockKey={block.key}
                  items={block.items}
                  onOpen={onOpenEntry}
                />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
